
import React from "react";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import { FileEdit, Copy, Trash, Pencil, MoreVertical, Star, RefreshCcw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/components/ui/use-toast";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { FloorPlanPreview } from "./FloorPlanPreview";
import { FolderSelect } from "./FolderSelect";
import { TagsInput } from "./TagsInput";
import { CategorySelect } from "./CategorySelect";
import { supabase } from "@/integrations/supabase/client";
import { generatePreview } from '@/utils/generatePreview';
import { activateFloorPlan } from "@/services/floorPlanService";

interface FloorPlanContent {
  nodes: any[];
  edges: any[];
}

interface FloorPlan {
  id: string;
  name: string;
  updated_at: string;
  content: FloorPlanContent;
  preview_image?: string | null;
  is_active: boolean;
  folder_id?: string | null;
  is_favorite?: boolean;
  is_public?: boolean;
}

interface FloorPlanCardProps {
  plan: FloorPlan;
  editingName: string | null;
  onEditName: (id: string) => void;
  onUpdateName: (id: string, newName: string) => void;
  onDuplicate: (plan: FloorPlan) => void;
  onDelete: (id: string) => void;
  onUpdateFolder: (id: string, folderId: string | null) => void;
  onToggleFavorite: (id: string, favorite: boolean) => void;
  onToggleVisibility?: (id: string, isPublic: boolean) => void;
  showCheckbox?: boolean;
  isSelected?: boolean;
  onSelect?: () => void;
}

export function FloorPlanCard({
  plan,
  editingName,
  onEditName,
  onUpdateName,
  onDuplicate,
  onDelete,
  onUpdateFolder,
  onToggleFavorite,
  onToggleVisibility,
  showCheckbox,
  isSelected,
  onSelect,
}: FloorPlanCardProps) {
  const navigate = useNavigate();
  const [isManageOpen, setIsManageOpen] = React.useState(false);
  const [isVisibilityDialogOpen, setIsVisibilityDialogOpen] = React.useState(false);
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [isNavigating, setIsNavigating] = React.useState(false);
  const previewIframeRef = React.useRef<HTMLIFrameElement | null>(null);
  const retryCountRef = React.useRef(0);
  const maxRetries = 5;

  const findCanvasElement = (document: Document): HTMLCanvasElement | null => {
    const canvas = document.querySelector('.react-flow__renderer');
    if (canvas instanceof HTMLCanvasElement) {
      return canvas;
    }
    return null;
  };

  const waitForCanvas = async (document: Document): Promise<HTMLCanvasElement> => {
    return new Promise((resolve, reject) => {
      const checkCanvas = () => {
        const canvas = findCanvasElement(document);
        if (canvas) {
          resolve(canvas);
        } else if (retryCountRef.current >= maxRetries) {
          reject(new Error('Kan canvas niet vinden na meerdere pogingen'));
        } else {
          retryCountRef.current++;
          setTimeout(checkCanvas, 1000);
        }
      };
      checkCanvas();
    });
  };

  const handleNavigateToEditor = async () => {
    try {
      // Prevent multiple navigation attempts
      if (isNavigating) return;
      setIsNavigating(true);
      
      console.log(`Navigating to editor with plan ID: ${plan.id}, current active status: ${plan.is_active}`);
      toast({
        title: "Plattegrond laden",
        description: "De plattegrond wordt voorbereid...",
      });
      
      if (!plan.is_active) {
        console.log(`Plan ${plan.id} is not active, attempting to activate...`);
        
        // Wait for the activation to be fully completed before proceeding
        await activateFloorPlan(plan.id);
        
        // Add a pause to ensure the database update is processed
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Verify that the floor plan is marked as active
        const { data, error } = await supabase
          .from("floor_plans")
          .select("is_active")
          .eq("id", plan.id)
          .single();
        
        if (error || !data || !data.is_active) {
          console.error("Verification failed, couldn't confirm plan activation:", error || "Plan not active");
          toast({
            title: "Waarschuwing",
            description: "Er was een probleem bij het activeren van de plattegrond. Probeer het opnieuw.",
            variant: "destructive",
          });
          setIsNavigating(false);
          return;
        } else {
          console.log(`Plan ${plan.id} successfully activated and verified`);
        }
      } else {
        console.log(`Plan ${plan.id} is already active, proceeding to editor`);
      }
      
      // Wait for navigation to be ready to prevent race conditions
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Add a final check for active state
      const { data: finalCheck, error: finalCheckError } = await supabase
        .from("floor_plans")
        .select("is_active")
        .eq("id", plan.id)
        .single();
        
      if (finalCheckError || !finalCheck || !finalCheck.is_active) {
        console.error("Final check failed, attempting emergency activation:", finalCheckError || "Plan not active");
        
        // Emergency activation
        const { error: emergencyError } = await supabase
          .from("floor_plans")
          .update({ is_active: true })
          .eq("id", plan.id);
          
        if (emergencyError) {
          console.error("Emergency activation failed:", emergencyError);
          toast({
            title: "Fout bij activeren",
            description: "Er is een fout opgetreden. Probeer het later opnieuw.",
            variant: "destructive",
          });
          setIsNavigating(false);
          return;
        }
        
        // Wait after emergency fix
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      // Use React Router navigate instead of window.location for smooth client-side navigation
      console.log(`FINAL: Navigating to editor with plan ID: ${plan.id}`);
      toast({
        title: "Plattegrond geopend",
        description: "De editor wordt geladen...",
      });
      
      // Navigate to the editor route with the plan ID parameter
      navigate(`/?id=${plan.id}`);
    } catch (error) {
      console.error("Error navigating to editor:", error);
      toast({
        title: "Fout bij openen",
        description: "Er is een fout opgetreden bij het openen van de plattegrond. Probeer het later opnieuw.",
        variant: "destructive",
      });
      setIsNavigating(false);
    }
  };

  const handleRefreshPreview = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);

    try {
      await generatePreview(plan.id);
      
      toast({
        title: "Preview bijgewerkt",
        description: "De preview is succesvol ververst.",
      });
    } catch (error: any) {
      console.error('Error refreshing preview:', error);
      toast({
        title: "Fout bij verversen",
        description: error.message || "Er is een fout opgetreden bij het verversen van de preview.",
        variant: "destructive",
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  React.useEffect(() => {
    return () => {
      if (previewIframeRef.current && previewIframeRef.current.parentNode) {
        previewIframeRef.current.parentNode.removeChild(previewIframeRef.current);
        previewIframeRef.current = null;
      }
    };
  }, []);

  return (
    <Card className="hover:border-emerald-200 transition-colors group">
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
        <div className="flex-1">
          {editingName === plan.id ? (
            <div className="flex gap-2">
              <Input
                defaultValue={plan.name}
                className="h-8"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    onUpdateName(plan.id, e.currentTarget.value);
                  } else if (e.key === 'Escape') {
                    onEditName(null);
                  }
                }}
                onBlur={(e) => onUpdateName(plan.id, e.target.value)}
                autoFocus
              />
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <CardTitle className="text-base font-medium">
                {plan.name}
              </CardTitle>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={() => onEditName(plan.id)}
              >
                <Pencil className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {showCheckbox && (
            <div className="flex items-center">
              <Checkbox
                checked={isSelected}
                onCheckedChange={onSelect}
                className="mr-2"
              />
            </div>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 p-0"
            onClick={() => onToggleFavorite(plan.id, !plan.is_favorite)}
          >
            <Star className={`h-4 w-4 ${plan.is_favorite ? 'fill-yellow-400 text-yellow-400' : ''}`} />
          </Button>
          <Dialog open={isManageOpen} onOpenChange={setIsManageOpen}>
            <DialogTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 p-0">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Plattegrond beheren</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Map</label>
                  <FolderSelect 
                    value={plan.folder_id || ''} 
                    onSelect={(folderId) => onUpdateFolder(plan.id, folderId)}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Categorieën</label>
                  <CategorySelect floorPlanId={plan.id} />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Tags</label>
                  <TagsInput floorPlanId={plan.id} />
                </div>
                <DropdownMenuSeparator />
                <div className="flex flex-col gap-2">
                  <Button
                    onClick={handleNavigateToEditor}
                    className="w-full gap-2 justify-start"
                    variant="outline"
                  >
                    <FileEdit className="h-4 w-4" /> Bewerken
                  </Button>
                  <Button
                    onClick={() => {
                      onDuplicate(plan);
                      setIsManageOpen(false);
                    }}
                    className="w-full gap-2 justify-start"
                    variant="outline"
                  >
                    <Copy className="h-4 w-4" /> Dupliceren
                  </Button>
                  <Button
                    onClick={() => {
                      onDelete(plan.id);
                      setIsManageOpen(false);
                    }}
                    className="w-full gap-2 justify-start text-red-600 hover:text-red-600"
                    variant="outline"
                  >
                    <Trash className="h-4 w-4" /> Verwijderen
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="relative">
          <FloorPlanPreview 
            id={plan.id}
            name={plan.name}
            previewImage={plan.preview_image}
          />
          <Button
            variant="outline"
            size="icon"
            className="absolute bottom-2 right-2 bg-white/80 hover:bg-white"
            onClick={handleRefreshPreview}
            disabled={isRefreshing}
          >
            <RefreshCcw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        <p className="text-sm text-muted-foreground">
          Laatst bewerkt: {format(new Date(plan.updated_at), "d MMMM yyyy 'om' HH:mm", {
            locale: nl,
          })}
        </p>
        <div className="mt-4">
          <Button 
            onClick={handleNavigateToEditor}
            className="w-full bg-emerald-50 text-[#1b9d3d] hover:bg-emerald-100"
            disabled={isNavigating}
          >
            {isNavigating 
              ? "Plattegrond openen..." 
              : (plan.is_active ? "Doorgaan met bewerken" : "Bekijk plattegrond")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
